"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Mail, Phone, MapPin, Clock, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact" ref={sectionRef} className={`py-24 md:py-32 bg-background transition-all duration-[var(--duration-medium)] ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <h2 className="text-[36px] md:text-[48px] leading-[1.2] font-bold tracking-[-0.2px] text-foreground mb-4">
            Contact Us
          </h2>
          <p className="text-[16px] leading-[1.6] text-muted-foreground max-w-2xl mx-auto">
            Get in touch with our accounting experts. We're here to help with your financial needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {}
          <div className="space-y-8">
            <div className="space-y-6">
              <h3 className="text-[28px] leading-[1.4] font-semibold tracking-[-0.2px] text-foreground mb-6">
                Get in Touch
              </h3>
              
              <ContactDetailItem icon={<Mail className="size-5" />} label="Email" value="contact@sloane.co.uk" />
              
              <ContactDetailItem icon={<Phone className="size-5" />} label="Phone" value="+44 20 7946 0958" />
              
              <ContactDetailItem icon={<MapPin className="size-5" />} label="Address" value="Sloane House, 123 Business Rd, London, SW1A 1AA, United Kingdom" />
              
              <ContactDetailItem icon={<Clock className="size-5" />} label="Hours" value="Monday to Friday: 9am - 5:30pm; Saturday & Sunday: Closed" />
            </div>
          </div>

          {}
          <div>
            <FormProvider className="bg-surface border border-border rounded-[var(--radius-sm)] p-8 shadow-soft" formId="contact_form_home" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for contacting us! We'll get back to you within 24 hours.">
              <ContactFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactDetailItem({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-start gap-4 group">
      <div className="flex-shrink-0 size-12 rounded-[var(--radius-md)] bg-primary-light flex items-center justify-center text-foreground transition-colors duration-[var(--duration-fast)] group-hover:bg-primary group-hover:text-foreground-primary">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-muted-foreground mb-1">{label}</p>
        <p className="text-[16px] leading-[1.6] text-foreground">{value}</p>
      </div>
    </div>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary-light border border-primary/20 rounded-[var(--radius-md)]">
        <p className="text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must be less than 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="rounded-[30px] focus-visible:shadow-[0_0_0_3px_hsla(221,83%,53%,0.3)] transition-shadow duration-[var(--duration-medium)]" />
              </FormControl>
              <FormDescription>Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="rounded-[30px] focus-visible:shadow-[0_0_0_3px_hsla(221,83%,53%,0.3)] transition-shadow duration-[var(--duration-medium)]" />
              </FormControl>
              <FormDescription>We'll use this to reply to your inquiry.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} className="rounded-[30px] focus-visible:shadow-[0_0_0_3px_hsla(221,83%,53%,0.3)] transition-shadow duration-[var(--duration-medium)]" />
              </FormControl>
              <FormDescription>Optional – include if you prefer a call back.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organisation</FormLabel>
              <FormControl>
                <Input placeholder="Acme Ltd." {...field} className="rounded-[30px] focus-visible:shadow-[0_0_0_3px_hsla(221,83%,53%,0.3)] transition-shadow duration-[var(--duration-medium)]" />
              </FormControl>
              <FormDescription>Your business name (if applicable).</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us how we can help..." {...field} className="rounded-[30px] min-h-32 focus-visible:shadow-[0_0_0_3px_hsla(221,83%,53%,0.3)] transition-shadow duration-[var(--duration-medium)]" />
              </FormControl>
              <FormDescription>Provide details of your accounting needs.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-[var(--radius-md)]">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-[var(--radius-full)] hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : 'Send Message'}
        </Button>
      </div>
    </Form>;
}